"use client";

import React, { useEffect, useState } from 'react';
import { Zap, Plus, Play, Settings, CheckCircle2, AlertCircle, Edit2, Trash2, Save, X, Pause, RotateCcw } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';

interface Automation {
  id: string;
  name: string;
  description?: string;
  trigger_type: string;
  trigger_config?: Record<string, any>;
  action_type: string;
  action_config?: Record<string, any>;
  is_active: boolean;
  last_run?: string;
  run_count: number;
  created_at: string;
  updated_at: string;
}

const Automations = () => {
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingAutomation, setEditingAutomation] = useState<Automation | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [testingAutomation, setTestingAutomation] = useState<string | null>(null);

  const triggerTypes = [
    { value: 'lead_created', label: 'Nuevo Lead Creado' },
    { value: 'client_added', label: 'Nuevo Cliente Agregado' },
    { value: 'workshop_scheduled', label: 'Workshop Programado' },
    { value: 'resource_uploaded', label: 'Recurso Subido' }
  ];

  const actionTypes = [
    { value: 'send_email', label: 'Enviar Email' },
    { value: 'create_task', label: 'Crear Tarea' },
    { value: 'notify_admin', label: 'Notificar Administrador' },
    { value: 'update_status', label: 'Actualizar Estado' }
  ];

  useEffect(() => {
    fetchAutomations();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('automations-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'automations' },
        () => fetchAutomations()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchAutomations = async () => {
    try {
      const { data, error } = await supabase
        .from('automations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (!error && data) setAutomations(data);
    } catch (error) {
      console.error('Error fetching automations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAutomation = async (automation: Automation) => {
    try {
      const automationData = {
        name: automation.name,
        description: automation.description,
        trigger_type: automation.trigger_type,
        trigger_config: automation.trigger_config,
        action_type: automation.action_type,
        action_config: automation.action_config,
        is_active: automation.is_active
      };

      if (editingAutomation?.id) {
        // Update existing automation
        const { error } = await supabase
          .from('automations')
          .update(automationData)
          .eq('id', automation.id);

        if (error) throw error;
      } else {
        // Create new automation
        const { error } = await supabase
          .from('automations')
          .insert(automationData);

        if (error) throw error;
      }

      setEditingAutomation(null);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving automation:', error);
    }
  };

  const handleDeleteAutomation = async (automationId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta automatización?')) return;
    
    try {
      const { error } = await supabase
        .from('automations')
        .delete()
        .eq('id', automationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting automation:', error);
    }
  };

  const handleToggleActive = async (automation: Automation) => {
    try {
      const { error } = await supabase
        .from('automations')
        .update({ is_active: !automation.is_active })
        .eq('id', automation.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error toggling automation:', error);
    }
  };

  const handleTestAutomation = async (automationId: string) => {
    setTestingAutomation(automationId);
    try {
      // Simulate testing the automation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update last run time
      await supabase
        .from('automations')
        .update({ 
          last_run: new Date().toISOString(),
          run_count: supabase.sql`run_count + 1`
        })
        .eq('id', automationId);

      alert('Automatización probada exitosamente');
    } catch (error) {
      console.error('Error testing automation:', error);
      alert('Error al probar la automatización');
    } finally {
      setTestingAutomation(null);
    }
  };

  const getTriggerTypeLabel = (type: string) => {
    return triggerTypes.find(t => t.value === type)?.label || type;
  };

  const getActionTypeLabel = (type: string) => {
    return actionTypes.find(a => a.value === type)?.label || type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-charcoal">Automatizaciones</h1>
          <p className="text-charcoal-muted">Flujos de trabajo y alertas automáticas.</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus size={20} /> Nueva Automatización
        </button>
      </div>

      <div className="grid gap-6">
        {automations.length > 0 ? automations.map((automation) => (
          <div key={automation.id} className="bg-white p-6 rounded-4xl border border-gray-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6 w-full md:w-auto">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                automation.is_active ? 'bg-blue-50 text-primary' : 'bg-gray-50 text-gray-400'
              }`}>
                <Zap size={28} fill={automation.is_active ? "currentColor" : "none"} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-charcoal">{automation.name}</h3>
                <p className="text-sm text-charcoal-muted">{automation.description}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-charcoal-muted">
                  <span className="flex items-center gap-1">
                    <AlertCircle size={12} />
                    {getTriggerTypeLabel(automation.trigger_type)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Settings size={12} />
                    {getActionTypeLabel(automation.action_type)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-8 w-full md:w-auto justify-between md:justify-end">
              <div className="text-right">
                <div className="text-xs text-charcoal-muted mb-1">Estado</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(automation)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl font-medium text-sm transition-colors ${
                      automation.is_active 
                        ? 'bg-green-50 text-green-600 hover:bg-green-100' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {automation.is_active ? <CheckCircle2 size={16} /> : <Pause size={16} />}
                    {automation.is_active ? 'Activa' : 'Inactiva'}
                  </button>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-xs text-charcoal-muted mb-1">Ejecuciones</div>
                <div className="text-sm font-medium text-charcoal">{automation.run_count}</div>
              </div>

              <div className="text-right">
                <div className="text-xs text-charcoal-muted mb-1">Última ejecución</div>
                <div className="text-sm font-medium text-charcoal">
                  {automation.last_run 
                    ? new Date(automation.last_run).toLocaleDateString('es-DO')
                    : 'Nunca'
                  }
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleTestAutomation(automation.id)}
                  disabled={testingAutomation === automation.id}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Probar automatización"
                >
                  {testingAutomation === automation.id ? (
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Play size={16} className="text-gray-400" />
                  )}
                </button>
                <button 
                  onClick={() => setEditingAutomation(automation)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Edit2 size={16} className="text-gray-400" />
                </button>
                <button 
                  onClick={() => handleDeleteAutomation(automation.id)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} className="text-red-400" />
                </button>
              </div>
            </div>
          </div>
        )) : (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-charcoal-muted">
            <Zap size={48} className="mb-4 opacity-20" />
            <p>No hay automatizaciones configuradas</p>
          </div>
        )}
      </div>

      {/* Add/Edit Automation Modal */}
      {(showAddModal || editingAutomation) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-charcoal">
                {editingAutomation ? 'Editar Automatización' : 'Nueva Automatización'}
              </h2>
              <button 
                onClick={() => {
                  setEditingAutomation(null);
                  setShowAddModal(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Nombre</label>
                <input
                  type="text"
                  value={editingAutomation?.name || ''}
                  onChange={(e) => setEditingAutomation(editingAutomation ? {...editingAutomation, name: e.target.value} : {
                    id: '',
                    name: e.target.value,
                    trigger_type: 'lead_created',
                    action_type: 'send_email',
                    is_active: true,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Descripción</label>
                <textarea
                  value={editingAutomation?.description || ''}
                  onChange={(e) => setEditingAutomation(editingAutomation ? {...editingAutomation, description: e.target.value} : {
                    id: '',
                    name: '',
                    description: e.target.value,
                    trigger_type: 'lead_created',
                    action_type: 'send_email',
                    is_active: true,
                    created_at: '',
                    updated_at: ''
                  })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Disparador</label>
                  <select
                    value={editingAutomation?.trigger_type || 'lead_created'}
                    onChange={(e) => setEditingAutomation(editingAutomation ? {...editingAutomation, trigger_type: e.target.value} : {
                      id: '',
                      name: '',
                      trigger_type: e.target.value,
                      action_type: 'send_email',
                      is_active: true,
                      created_at: '',
                      updated_at: ''
                    })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {triggerTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Acción</label>
                  <select
                    value={editingAutomation?.action_type || 'send_email'}
                    onChange={(e) => setEditingAutomation(editingAutomation ? {...editingAutomation, action_type: e.target.value} : {
                      id: '',
                      name: '',
                      trigger_type: 'lead_created',
                      action_type: e.target.value,
                      is_active: true,
                      created_at: '',
                      updated_at: ''
                    })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {actionTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Configuración del Disparador</label>
                  <textarea
                    value={editingAutomation?.trigger_config ? JSON.stringify(editingAutomation.trigger_config, null, 2) : ''}
                    onChange={(e) => {
                      try {
                        const config = JSON.parse(e.target.value);
                        setEditingAutomation(editingAutomation ? {...editingAutomation, trigger_config: config} : {
                          id: '',
                          name: '',
                          trigger_type: 'lead_created',
                          trigger_config: config,
                          action_type: 'send_email',
                          is_active: true,
                          created_at: '',
                          updated_at: ''
                        });
                      } catch (error) {
                        // Invalid JSON, ignore
                      }
                    }}
                    rows={4}
                    placeholder='{"key": "value"}'
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Configuración de la Acción</label>
                  <textarea
                    value={editingAutomation?.action_config ? JSON.stringify(editingAutomation.action_config, null, 2) : ''}
                    onChange={(e) => {
                      try {
                        const config = JSON.parse(e.target.value);
                        setEditingAutomation(editingAutomation ? {...editingAutomation, action_config: config} : {
                          id: '',
                          name: '',
                          trigger_type: 'lead_created',
                          action_type: 'send_email',
                          action_config: config,
                          is_active: true,
                          created_at: '',
                          updated_at: ''
                        });
                      } catch (error) {
                        // Invalid JSON, ignore
                      }
                    }}
                    rows={4}
                    placeholder='{"key": "value"}'
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editingAutomation?.is_active ?? true}
                  onChange={(e) => setEditingAutomation(editingAutomation ? {...editingAutomation, is_active: e.target.checked} : {
                    id: '',
                    name: '',
                    trigger_type: 'lead_created',
                    action_type: 'send_email',
                    is_active: e.target.checked,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-charcoal">
                  Activar automatización
                </label>
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setEditingAutomation(null);
                  setShowAddModal(false);
                }}
                className="flex-1 px-6 py-3 border border-gray-200 text-charcoal rounded-2xl font-bold hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => editingAutomation && handleSaveAutomation(editingAutomation)}
                className="flex-1 btn-primary"
              >
                <Save size={20} /> {editingAutomation ? 'Guardar Cambios' : 'Crear Automatización'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Automations;